/**
 * The chat surface used by /chat, /chat/:threadId and the "Ask Mimic" drawer (`compact`).
 *   • no thread  → agent home (PlayAd "Hey! What would you like to do?" hero, composer, Research / Analyze /
 *                  Create / Launch chips + template panels) — or a compact "ask about this page" start in the drawer.
 *   • thread     → header (title, schedule, history), message list, composer, and on desktop a right split pane
 *                  for report artifacts / flows (`?report=` / `?flow=` in the URL). Compact mode opens reports full-page.
 * Runs stream through the module-level runtime (./runtime) so navigation never drops a reply.
 */
import { useQueryClient } from "@tanstack/react-query";
import { ArrowDown, CalendarClock, ChevronRight, FileText, History, MessageSquare, PencilLine, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { markThreadRead, updateThread, type ChatAttachment, type ChatMessage, type ChatThread, type SendMessageBody } from "@/api/chat";
import { threadKey, threadsKey, useChatTemplates, useThread } from "@/api/chat-hooks";
import { ApiError, errorMessage } from "@/api/client";
import { toast } from "@/api/toast-store";
import { cn } from "@/lib/cn";
import { FlowPanelSlot, flowEditorPath } from "@/lib/flows";
import { brandPath, useCurrentBrand } from "@/state/brand";
import { Button, IconButton } from "../ui/Button";
import { EmptyState, ErrorState, Skeleton } from "../ui/Feedback";
import { MenuLabel, PopoverPanel, Tooltip, usePopover } from "../ui/Overlay";
import { RobotFace } from "../ui/Page";
import type { AttachmentCtx } from "./Attachments";
import { Composer, type ComposerHandle, type ComposerSettingsState, type ComposerSubmit } from "./Composer";
import { buildRows, collectToolResults, MessageList } from "./MessageList";
import { ArtifactViewer } from "./report/ArtifactViewer";
import { clearError, createAndSend, reattach, sendMessage, stopRun, useLive } from "./runtime";
import { ASK_SUGGESTIONS, ROTATING_PLACEHOLDERS, TEMPLATE_GROUPS, type TemplateGroup } from "./templates";
import { asAutonomy, asLevel, asMode, obj, str, timeLabel, useComposerPrefs, useMediaQuery } from "./util";

export interface ChatViewProps {
  threadId?: string | null;
  brandId?: string | null;
  /** Page context attached to outgoing messages (Ask drawer). */
  context?: Record<string, unknown>;
  /** Prefill (or auto-send when `autoSend`) the composer. */
  initialPrompt?: string;
  autoSend?: boolean;
  /** Compact layout for the drawer (no split artifact pane; artifacts open full-page). */
  compact?: boolean;
  onThreadCreated?: (id: string) => void;
}

const AGENT = "Mimic";

export function ChatView(props: ChatViewProps) {
  const { brandId } = useCurrentBrand();
  const selected = props.brandId ?? brandId;
  return <SelectedChatView key={selected ?? "none"} {...props} brandId={selected} />;
}

function SelectedChatView(props: ChatViewProps) {
  const [threadId, setThreadId] = useState<string | null>(props.threadId ?? null);
  useEffect(() => {
    if (props.threadId !== undefined) setThreadId(props.threadId ?? null);
  }, [props.threadId]);
  const onCreated = useCallback(
    (id: string) => {
      setThreadId(id);
      props.onThreadCreated?.(id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.onThreadCreated],
  );
  if (!threadId) {
    return <ChatHome compact={props.compact} brandId={props.brandId ?? null} context={props.context} initialPrompt={props.initialPrompt} autoSend={props.autoSend} onCreated={onCreated} />;
  }
  return <ThreadView key={threadId} threadId={threadId} compact={props.compact} brandIdFallback={props.brandId ?? null} />;
}

// ─── Shared settings (autonomy + Instant/Thinking) ─────────────────────────

function useChatSettings(thread: ChatThread | null, threadId: string | null): [ComposerSettingsState, (p: Partial<ComposerSettingsState>) => void] {
  const qc = useQueryClient();
  const [prefs, setPrefs] = useComposerPrefs();
  const [local, setLocal] = useState<Partial<ComposerSettingsState>>({});
  const value: ComposerSettingsState = {
    autonomy: local.autonomy ?? asAutonomy(thread?.autonomy) ?? prefs.autonomy,
    mode: local.mode ?? asMode(thread?.mode) ?? prefs.mode,
    thinkingLevel: local.thinkingLevel ?? asLevel(thread?.thinkingLevel) ?? prefs.thinkingLevel,
  };
  const set = useCallback(
    (patch: Partial<ComposerSettingsState>) => {
      setLocal((l) => ({ ...l, ...patch }));
      setPrefs(patch);
      if (threadId) {
        void updateThread(threadId, patch)
          .then((t) => {
            if (t) void qc.invalidateQueries({ queryKey: threadKey(threadId) });
          })
          .catch(() => {
            /* kept locally; sent with the next message as composer settings */
          });
      }
    },
    [qc, setPrefs, threadId],
  );
  return [value, set];
}

function bodyFrom(s: ComposerSubmit): { body: SendMessageBody; display: ChatAttachment[] } {
  return {
    body: { content: s.text || "See the attached media.", composer: s.composer, ...(s.attachments.length ? { attachments: s.attachments } : {}) },
    display: s.display,
  };
}

// ─── Agent home ────────────────────────────────────────────────────────────

function useTemplateGroups(brandName: string | null): TemplateGroup[] {
  const t = useChatTemplates();
  return useMemo(() => {
    const server = t.data?.groups ?? [];
    if (!server.length) return TEMPLATE_GROUPS;
    const name = brandName ?? "my brand";
    return server.map((g) => {
      const fallback = TEMPLATE_GROUPS.find((x) => x.id === g.id);
      return {
        id: (g.id as TemplateGroup["id"]) ?? "research",
        label: g.label,
        color: fallback?.color ?? g.color ?? "var(--chip-launch)",
        templates: g.templates.map((x) => ({ label: x.title, prompt: x.prompt.replace(/\{brand\}/g, name) })),
      };
    });
  }, [t.data, brandName]);
}

function ChatHome({
  compact,
  brandId,
  context,
  initialPrompt,
  autoSend,
  onCreated,
}: {
  compact?: boolean;
  brandId: string | null;
  context?: Record<string, unknown>;
  initialPrompt?: string;
  autoSend?: boolean;
  onCreated: (id: string) => void;
}) {
  const qc = useQueryClient();
  const { brand } = useCurrentBrand();
  const effectiveBrand = brandId ?? brand?.id ?? null;
  const groups = useTemplateGroups(brand?.name ?? null);
  const templates = useChatTemplates();
  const [settings, setSettings] = useChatSettings(null, null);
  const composerRef = useRef<ComposerHandle | null>(null);
  const [panel, setPanel] = useState<TemplateGroup["id"] | null>(null);
  const [starting, setStarting] = useState(false);
  const sentRef = useRef(false);

  const start = useCallback(
    async (s: ComposerSubmit) => {
      setStarting(true);
      try {
        const { body, display } = bodyFrom(s);
        await createAndSend(
          qc,
          { brandId: effectiveBrand, autonomy: settings.autonomy, mode: settings.mode, thinkingLevel: settings.thinkingLevel, ...(context ? { context } : {}) },
          body,
          { display, onCreated: (t) => onCreated(t.id) },
        );
      } finally {
        setStarting(false);
      }
    },
    [qc, effectiveBrand, settings, context, onCreated],
  );

  const quickSend = (text: string) =>
    start({ text, attachments: [], display: [], composer: { mode: settings.mode, ...(settings.mode === "thinking" ? { thinkingLevel: settings.thinkingLevel } : {}), autonomy: settings.autonomy } }).catch(() => {
      composerRef.current?.setText(text);
    });

  useEffect(() => {
    if (!initialPrompt || sentRef.current) return;
    sentRef.current = true;
    if (autoSend) void quickSend(initialPrompt);
    else composerRef.current?.setText(initialPrompt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrompt, autoSend]);

  const composer = (
    <Composer
      ref={composerRef}
      brandId={effectiveBrand}
      placeholders={compact ? ["Ask about what you're seeing…"] : ROTATING_PLACEHOLDERS}
      settings={settings}
      onSettings={setSettings}
      contextWindow={templates.data?.contextBudget}
      onSend={start}
      compact={compact}
      autoFocus
      disabled={starting || !effectiveBrand}
    />
  );

  if (compact) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex min-h-0 flex-1 flex-col justify-end gap-4 overflow-y-auto px-4 py-5 scrollbar-thin">
          <div className="flex items-center gap-3">
            <RobotFace color="#d9773f" className="size-8" />
            <div>
              <div className="text-[15px] font-semibold">Ask {AGENT} about this page</div>
              <div className="text-[12.5px] text-muted">{AGENT} sees what you're looking at.</div>
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            {ASK_SUGGESTIONS.map((s) => (
              <button key={s} type="button" disabled={starting} onClick={() => void quickSend(s)} className="flex items-center justify-between gap-2 rounded-xl border border-line bg-surface px-3.5 py-2.5 text-left text-[13.5px] text-fg/90 transition-colors hover:border-line-strong hover:bg-hover disabled:opacity-50">
                {s}
                <ChevronRight className="size-4 shrink-0 text-muted" />
              </button>
            ))}
          </div>
        </div>
        <div className="shrink-0 px-3 pb-3">{composer}</div>
      </div>
    );
  }

  const active = groups.find((g) => g.id === panel) ?? null;
  return (
    <div className="absolute inset-0 overflow-y-auto scrollbar-thin">
      <div className={cn("mx-auto w-full max-w-[760px] px-4 pb-16 transition-[padding] duration-300 ease-out sm:px-6", active ? "pt-[9vh]" : "pt-[max(9vh,calc(50vh-190px))]")}>
        <h1 className="mb-7 text-center text-[28px] font-medium tracking-[-0.02em] text-fg sm:text-[34px]">Hey! What would you like to do?</h1>
        {composer}
        {active ? (
          <div className="mt-6 animate-rise rounded-2xl border border-line bg-surface p-2">
            <div className="flex items-center justify-between px-3 pb-1.5 pt-2">
              <span className="flex items-center gap-2 text-[13.5px] text-fg/90">
                <RobotFace color={active.color} className="size-4.5" />
                {active.label}
              </span>
              <IconButton label="Close" size="xs" onClick={() => setPanel(null)}>
                <X className="size-3.5" />
              </IconButton>
            </div>
            <ul>
              {active.templates.map((t) => (
                <li key={t.label}>
                  <button type="button" onClick={() => composerRef.current?.setText(t.prompt)} className="w-full rounded-xl px-3 py-2.5 text-left text-[14px] text-fg/90 transition-colors hover:bg-hover">
                    {t.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="mt-7 flex flex-wrap justify-center gap-2.5">
            {groups.map((g) => (
              <button
                key={g.id}
                type="button"
                onClick={() => setPanel(g.id)}
                className="inline-flex h-10 items-center gap-2 rounded-full border border-line bg-[#252527] pl-3 pr-4 text-[14.5px] text-fg/90 transition-colors hover:border-line-strong hover:bg-[#2b2b2d]"
              >
                <RobotFace color={g.color} className="size-5" />
                {g.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Thread ────────────────────────────────────────────────────────────────

type Pane = { type: "report"; id: string } | { type: "flow"; id: string; title?: string; brandId?: string | null };

function ThreadView({ threadId, compact, brandIdFallback }: { threadId: string; compact?: boolean; brandIdFallback: string | null }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const rawLive = useLive(threadId);
  const templates = useChatTemplates();
  const [sp, setSp] = useSearchParams();
  const wide = useMediaQuery("(min-width: 1024px)");
  const q = useThread(threadId, { frozen: rawLive.streaming, brandId: brandIdFallback });
  const live = useLive(q.data?.thread?.brandId === brandIdFallback ? threadId : null);
  const thread = q.data?.thread ?? null;
  const persisted = q.data?.messages ?? [];
  const serverRunning = !live.streaming && thread?.status === "running";
  const running = live.streaming || serverRunning;
  const brandId = thread?.brandId ?? brandIdFallback ?? null;
  const agentName = templates.data?.agentName || AGENT;
  const [settings, setSettings] = useChatSettings(thread, threadId);
  const composerRef = useRef<ComposerHandle | null>(null);

  // Re-attach once to a run that's live on the server (reload / came back to the page).
  const reattached = useRef(false);
  useEffect(() => {
    if (thread?.status === "running" && !live.streaming && !reattached.current) {
      reattached.current = true;
      void reattach(qc, threadId);
    }
  }, [thread?.status, live.streaming, qc, threadId]);

  // Mark read when the thread is visible and settled.
  const lastMarked = useRef<string | null>(null);
  useEffect(() => {
    if (!thread?.unread || live.streaming || document.visibilityState !== "visible") return;
    const k = `${thread.lastMessageAt ?? ""}`;
    if (lastMarked.current === k) return;
    lastMarked.current = k;
    void markThreadRead(threadId).then(() => qc.invalidateQueries({ queryKey: threadsKey }));
  }, [thread?.unread, thread?.lastMessageAt, live.streaming, qc, threadId]);

  // Right pane: ?report= / ?flow= on desktop full page; compact (drawer) and mobile navigate instead.
  const reportParam = sp.get("report");
  const flowParam = sp.get("flow");
  const pane: Pane | null = !thread || thread.brandId !== brandIdFallback || compact || !wide ? null : reportParam ? { type: "report", id: reportParam } : flowParam ? { type: "flow", id: flowParam, title: sp.get("flowTitle") ?? undefined, brandId } : null;
  const setPane = useCallback(
    (p: Pane | null) => {
      setSp(
        (prev) => {
          const n = new URLSearchParams(prev);
          n.delete("report");
          n.delete("flow");
          n.delete("flowTitle");
          if (p?.type === "report") n.set("report", p.id);
          if (p?.type === "flow") {
            n.set("flow", p.id);
            if (p.title) n.set("flowTitle", p.title);
          }
          return n;
        },
        { replace: true },
      );
    },
    [setSp],
  );

  const ctx: AttachmentCtx = useMemo(
    () => ({
      threadId,
      brandId,
      compact,
      activeArtifactId: pane?.type === "report" ? pane.id : null,
      onOpenArtifact: (id: string) => {
        if (compact || !wide) nav(`/reports/${id}`);
        else setPane({ type: "report", id });
      },
      onOpenFlow: (f) => {
        if (compact || !wide) nav(flowEditorPath(f.brandId ?? brandId, f.flowId));
        else setPane({ type: "flow", id: f.flowId, title: f.title, brandId: f.brandId });
      },
    }),
    [threadId, brandId, compact, pane, wide, nav, setPane],
  );

  const rows = useMemo(() => buildRows(persisted, live, thread, serverRunning), [persisted, live, thread, serverRunning]);
  const toolResults = useMemo(() => collectToolResults(persisted, live.toolResults), [persisted, live.toolResults]);

  // ── Scrolling ──
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [atBottom, setAtBottom] = useState(true);
  const atBottomRef = useRef(true);
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const b = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    atBottomRef.current = b;
    setAtBottom(b);
  };
  const scrollToBottom = (smooth = false) => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
  };
  const contentSig = useMemo(() => {
    const last = live.items[live.items.length - 1];
    const tail = last?.kind === "draft" ? last.segs.reduce((n, s) => n + (s.kind === "text" ? s.text.length : 50), 0) : 0;
    return `${rows.length}:${tail}:${live.phase}:${live.statusLabel ?? ""}`;
  }, [rows.length, live.items, live.phase, live.statusLabel]);
  const firstScroll = useRef(true);
  useEffect(() => {
    if (!q.data && !live.items.length) return;
    if (firstScroll.current) {
      firstScroll.current = false;
      requestAnimationFrame(() => scrollToBottom());
      return;
    }
    if (atBottomRef.current) requestAnimationFrame(() => scrollToBottom());
  }, [contentSig, q.data]);

  const send = async (s: ComposerSubmit) => {
    clearError(threadId);
    const { body, display } = bodyFrom(s);
    atBottomRef.current = true;
    await sendMessage(qc, threadId, body, { display });
  };

  const retryLast = () => {
    clearError(threadId);
    void sendMessage(qc, threadId, { content: "Please try that again.", composer: { mode: settings.mode, ...(settings.mode === "thinking" ? { thinkingLevel: settings.thinkingLevel } : {}), autonomy: settings.autonomy } });
  };

  const notFound = q.error instanceof ApiError && (q.error.status === 404 || q.error.status === 403);

  const header = !compact && (
    <ThreadHeader thread={thread} threadId={threadId} loading={q.isLoading} messages={persisted} brandId={brandId} onOpenArtifact={ctx.onOpenArtifact} />
  );

  const body = (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div ref={scrollRef} onScroll={onScroll} className="min-h-0 flex-1 overflow-y-auto scrollbar-thin" aria-busy={running}>
        <div className={cn("mx-auto w-full px-4 pb-8", compact ? "pt-4" : pane ? "max-w-[760px] pt-6 sm:px-6" : "max-w-[820px] pt-6 sm:px-8")}>
          {q.isLoading && !live.items.length ? (
            <ThreadSkeleton />
          ) : notFound ? (
            <EmptyState
              icon={<MessageSquare className="size-5" />}
              title="Chat not found"
              body="This chat doesn't exist or isn't shared with you."
              action={
                <Button variant="primary" onClick={() => nav("/chat")}>
                  Start a new chat
                </Button>
              }
            />
          ) : q.error && !q.data && !live.items.length ? (
            <ErrorState error={q.error} onRetry={() => void q.refetch()} />
          ) : rows.length === 0 && !running ? (
            <EmptyState compact icon={<RobotFace color="#d9773f" className="size-6" />} title="No messages yet" body={`Ask ${agentName} anything about this brand.`} />
          ) : (
            <MessageList rows={rows} live={live} thread={thread} threadId={threadId} serverRunning={serverRunning} toolResults={toolResults} ctx={ctx} agentName={agentName} compact={compact} onRetryError={retryLast} />
          )}
        </div>
      </div>
      {!atBottom && (
        <button
          type="button"
          onClick={() => scrollToBottom(true)}
          aria-label="Scroll to latest"
          className="absolute bottom-3 left-1/2 z-10 -translate-x-1/2 animate-fade-in rounded-full border border-line-strong bg-elevated/95 p-2 text-fg shadow-pop backdrop-blur hover:bg-elevated-2"
        >
          <ArrowDown className="size-4" />
        </button>
      )}
    </div>
  );

  const composer = (
    <div className={cn("shrink-0 bg-gradient-to-t from-canvas via-canvas to-transparent", compact ? "px-3 pb-3 pt-2" : "px-4 pb-4 pt-2 sm:px-8")}>
      <div className={cn("mx-auto w-full", !compact && (pane ? "max-w-[760px]" : "max-w-[820px]"))}>
        <Composer
          ref={composerRef}
          brandId={brandId}
          running={running}
          stopping={live.stopping}
          settings={settings}
          onSettings={setSettings}
          contextTokens={live.contextTokens ?? thread?.contextTokens ?? 0}
          contextWindow={live.contextWindow ?? templates.data?.contextBudget}
          onSend={send}
          onStop={() => void stopRun(qc, threadId)}
          compact={compact}
          autoFocus={!compact}
          disabled={notFound || !thread || thread.brandId !== brandIdFallback}
        />
      </div>
    </div>
  );

  const left = (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      {header}
      {body}
      {composer}
    </div>
  );

  if (compact) return left;
  return (
    <div className={cn("absolute inset-0 grid min-h-0", pane ? "lg:grid-cols-[minmax(380px,0.95fr)_minmax(460px,1.1fr)]" : "grid-cols-1")}>
      {left}
      {pane && (
        <aside className="hidden min-h-0 border-l border-line lg:block" aria-label={pane.type === "report" ? "Report" : "Flow"}>
          {pane.type === "report" ? (
            <ArtifactViewer
              artifactId={pane.id}
              mode="pane"
              brandId={brandId}
              onClose={() => setPane(null)}
              onNavigate={(id) => setPane({ type: "report", id })}
              onExpand={() => nav(`/reports/${pane.id}`)}
            />
          ) : (
            <div className="h-full bg-[#161618]">
              <FlowPanelSlot flowId={pane.id} brandId={pane.brandId ?? brandId} title={pane.title} onClose={() => setPane(null)} />
            </div>
          )}
        </aside>
      )}
    </div>
  );
}

function ThreadSkeleton() {
  return (
    <div className="flex flex-col gap-7" aria-hidden>
      <Skeleton className="ml-auto h-14 w-[55%] rounded-2xl" />
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-[92%]" />
        <Skeleton className="h-4 w-[85%]" />
        <Skeleton className="h-4 w-[60%]" />
      </div>
      <Skeleton className="ml-auto h-10 w-[40%] rounded-2xl" />
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-[88%]" />
        <Skeleton className="h-4 w-[70%]" />
      </div>
    </div>
  );
}

// ─── Header: title (rename), schedule, history ─────────────────────────────

function ThreadHeader({
  thread,
  threadId,
  loading,
  messages,
  brandId,
  onOpenArtifact,
}: {
  thread: ChatThread | null;
  threadId: string;
  loading: boolean;
  messages: ChatMessage[];
  brandId: string | null;
  onOpenArtifact: (id: string) => void;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [localTitle, setLocalTitle] = useState<string | null>(null);
  const title = localTitle ?? thread?.title ?? "";
  const scheduled = thread?.kind === "scheduled";
  const save = async () => {
    const t = draft.trim();
    setEditing(false);
    if (!t || t === title) return;
    setLocalTitle(t);
    try {
      await updateThread(threadId, { title: t });
      void qc.invalidateQueries({ queryKey: threadKey(threadId) });
      void qc.invalidateQueries({ queryKey: threadsKey });
    } catch (e) {
      setLocalTitle(null);
      toast({ tone: "danger", title: "Couldn't rename the chat", body: errorMessage(e) });
    }
  };
  return (
    <div className="flex h-14 shrink-0 items-center gap-2 px-4 sm:px-6">
      {loading && !thread ? (
        <Skeleton className="h-5 w-56" />
      ) : editing ? (
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => void save()}
          onKeyDown={(e) => {
            if (e.key === "Enter") void save();
            if (e.key === "Escape") setEditing(false);
          }}
          aria-label="Chat title"
          className="h-9 w-full max-w-md rounded-lg border border-primary bg-surface-2 px-2.5 text-[15px] font-semibold outline-none"
        />
      ) : (
        <button
          type="button"
          onClick={() => {
            setDraft(title);
            setEditing(true);
          }}
          className="group flex min-w-0 items-center gap-2 rounded-lg px-1 py-1 text-left hover:bg-hover"
          title="Rename"
        >
          <h1 className="truncate text-[16px] font-semibold tracking-[-0.01em]">{title || "New chat"}</h1>
          <PencilLine className="size-3.5 shrink-0 text-subtle opacity-0 transition-opacity group-hover:opacity-100" />
        </button>
      )}
      {scheduled && (
        <Tooltip content="Scheduled chat — open the Scheduler" side="bottom">
          <Link to={brandPath(brandId, "scheduler")} aria-label="Scheduled chat" className="grid size-8 shrink-0 place-items-center rounded-lg text-muted hover:bg-hover hover:text-fg">
            <CalendarClock className="size-4" />
          </Link>
        </Tooltip>
      )}
      <HistoryMenu messages={messages} onOpenArtifact={onOpenArtifact} />
    </div>
  );
}

function HistoryMenu({ messages, onOpenArtifact }: { messages: ChatMessage[]; onOpenArtifact: (id: string) => void }) {
  const p = usePopover();
  const { runs, reports } = useMemo(() => {
    const runs: { id: string; text: string; at?: string }[] = [];
    const reports: { id: string; title: string; at?: string; revision?: number }[] = [];
    for (const m of messages) {
      if (m.role === "user" && !m.meta?.context && !m.meta?.answersFor) {
        const text = m.content
          .filter((b) => b.type === "text")
          .map((b) => String((b as { text?: unknown }).text ?? ""))
          .find((t) => t.trim() && !t.trim().startsWith("<context>"));
        if (text) runs.push({ id: m.id, text, at: m.createdAt });
      }
      for (const a of m.attachments ?? []) {
        if (a.kind !== "artifact") continue;
        const d = obj(a.data) ?? {};
        const id = str(d.artifactId) ?? a.id;
        if (id && !reports.some((r) => r.id === id)) reports.push({ id, title: str(d.title) ?? "Report", at: str(d.publishedAt) ?? m.createdAt, revision: typeof d.revision === "number" ? d.revision : undefined });
      }
    }
    return { runs: runs.reverse(), reports: reports.reverse() };
  }, [messages]);
  return (
    <>
      <Tooltip content="History" side="bottom">
        <button
          type="button"
          ref={(el) => {
            p.anchorRef.current = el;
          }}
          onClick={p.toggle}
          aria-label="History"
          aria-expanded={p.open}
          className={cn("grid size-8 shrink-0 place-items-center rounded-lg text-muted hover:bg-hover hover:text-fg", p.open && "bg-hover text-fg")}
        >
          <History className="size-4" />
        </button>
      </Tooltip>
      <PopoverPanel open={p.open} onClose={p.close} anchorRef={p.anchorRef} className="max-h-[60vh] w-80 overflow-y-auto p-1.5 scrollbar-thin">
        {reports.length > 0 && (
          <>
            <MenuLabel>Reports</MenuLabel>
            {reports.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => {
                  p.close();
                  onOpenArtifact(r.id);
                }}
                className="flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-hover"
              >
                <FileText className="mt-0.5 size-4 shrink-0 text-lavender" />
                <span className="min-w-0">
                  <span className="block truncate text-[13px]">{r.title}</span>
                  <span className="block text-[11.5px] text-muted">
                    {r.revision ? `Revision ${r.revision} · ` : ""}
                    {timeLabel(r.at)}
                  </span>
                </span>
              </button>
            ))}
          </>
        )}
        <MenuLabel>Runs</MenuLabel>
        {runs.length === 0 && <div className="px-2.5 py-2 text-[12.5px] text-muted">No runs yet.</div>}
        {runs.map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => {
              p.close();
              document.getElementById(`row-${r.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="flex w-full flex-col rounded-lg px-2.5 py-2 text-left hover:bg-hover"
          >
            <span className="line-clamp-2 text-[13px] text-fg/90">{r.text}</span>
            <span className="text-[11.5px] text-muted">{timeLabel(r.at)}</span>
          </button>
        ))}
      </PopoverPanel>
    </>
  );
}
